import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

const LegalPagesTab = ({ authAxios }) => {
  const [privacyPolicy, setPrivacyPolicy] = useState({ enabled: true, introduction: '', sections: [], last_updated: '' });
  const [termsOfService, setTermsOfService] = useState({ enabled: true, introduction: '', sections: [], last_updated: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLegalPages();
  }, [authAxios]);

  const fetchLegalPages = async () => {
    try {
      const [privacyRes, termsRes] = await Promise.all([
        authAxios.get('/admin/privacy-policy'),
        authAxios.get('/admin/terms-of-service')
      ]);
      setPrivacyPolicy(privacyRes.data);
      setTermsOfService(termsRes.data);
    } catch (error) {
      console.error('Failed to fetch legal pages:', error);
    }
  };

  const renderLegalSection = (title, subtitle, viewUrl, data, setData, saveEndpoint, toggleEndpoint) => (
    <div className="bg-[#111111] border border-white/5 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-mono font-bold text-lg">{title}</h2>
          <p className="text-white/50 text-xs mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-4">
          <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-xs font-mono hover:underline">View Page</a>
          <button
            onClick={async () => {
              try {
                const res = await authAxios.patch(toggleEndpoint);
                setData({...data, enabled: res.data.enabled});
                toast.success(`${title} ${res.data.enabled ? 'enabled' : 'disabled'}`);
              } catch { toast.error(`Failed to toggle ${title.toLowerCase()}`); }
            }}
            className={`px-4 py-2 text-xs font-mono font-bold transition-colors ${
              data.enabled 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30' 
                : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
            }`}
          >
            {data.enabled ? 'ENABLED' : 'DISABLED'}
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-white/50 text-xs font-mono uppercase block mb-2">Introduction</label>
          <textarea
            placeholder={`Your ${title.toLowerCase()} introduction...`}
            value={data.introduction || ''}
            onChange={(e) => setData({...data, introduction: e.target.value})}
            rows={3}
            className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none resize-none"
          />
        </div>
        
        <div className="text-white/50 text-xs font-mono uppercase mb-2">Sections</div>
        <div className="space-y-4">
          {(data.sections || []).map((section, index) => (
            <div key={index} className="p-4 bg-[#0A0A0A] border border-white/10 rounded space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[#D4AF37] text-xs font-mono">Section {index + 1}</span>
                <button onClick={() => {
                  const newSections = data.sections.filter((_, i) => i !== index);
                  setData({...data, sections: newSections});
                }} className="text-red-400/60 hover:text-red-400 text-xs">Remove</button>
              </div>
              <input
                type="text"
                value={section.title}
                onChange={(e) => {
                  const newSections = [...data.sections];
                  newSections[index] = {...section, title: e.target.value};
                  setData({...data, sections: newSections});
                }}
                placeholder="Section Title"
                className="w-full bg-[#111111] border border-white/10 px-3 py-2 text-sm font-bold focus:border-[#D4AF37] outline-none"
              />
              <textarea
                value={section.content}
                onChange={(e) => {
                  const newSections = [...data.sections];
                  newSections[index] = {...section, content: e.target.value};
                  setData({...data, sections: newSections});
                }}
                placeholder="Section content..."
                rows={4}
                className="w-full bg-[#111111] border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none resize-none"
              />
            </div>
          ))}
        </div>
        
        <button
          onClick={() => setData({...data, sections: [...(data.sections || []), {title: '', content: ''}]})}
          className="w-full py-3 border border-dashed border-white/20 text-white/50 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm font-mono"
        >
          + Add Section
        </button>
        
        {data.last_updated && <p className="text-white/30 text-xs">Last updated: {data.last_updated}</p>}
        
        <button
          onClick={async () => {
            setSaving(true);
            try {
              await authAxios.put(saveEndpoint, data);
              toast.success(`${title} updated`);
              fetchLegalPages();
            } catch { toast.error(`Failed to update ${title.toLowerCase()}`); }
            finally { setSaving(false); }
          }}
          disabled={saving}
          className="px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : `Save ${title}`}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl space-y-6" data-testid="legal-pages-tab">
      {renderLegalSection(
        'Privacy Policy', 'Manage your privacy policy page content', '/privacy-policy',
        privacyPolicy, setPrivacyPolicy, '/admin/privacy-policy', '/admin/privacy-policy/toggle'
      )}
      {renderLegalSection(
        'Terms of Service', 'Manage your terms of service page content', '/terms-of-service',
        termsOfService, setTermsOfService, '/admin/terms-of-service', '/admin/terms-of-service/toggle'
      )}
    </div>
  );
};

export default LegalPagesTab;
